/**
 * AboveUCitationsView.jsx
 *
 * MINSKY-17131 whole-document view: every above-(U) citation currently in the report, each with
 * a lazy "Find (U) alternative" action, plus a single bulk "Find alternatives for all" action
 * (bounded concurrency) that fires the same per-citation search for every listed citation.
 *
 * Mirrors the margin rail's per-citation flow (Phase 3) — same `AlternativeCitationSuggestion`
 * component, same Replace / Add-alongside actions — so a result found here is also visible if
 * the analyst later opens that citation in the margin rail, and vice versa (both read/write the
 * same Y.Map).
 */

import React, {useCallback, useMemo, useRef, useState} from 'react';
import {useParams} from 'react-router';
import {Alert, Box, Button, Chip, List, ListItem, Stack, Typography} from '@mui/material';
import FindReplaceIcon from '@mui/icons-material/FindReplace';
import useYDocStore from '../../../../../stores/useYDocStore';
import {useAboveUCitations} from '../../../../../hooks/useAboveUCitations';
import {addReference, removeReferenceMark} from '../../../../../components/prosemirror-editor/yMaps/referencesYMap';
import {buildReferenceDataFromCitation} from '../../../../../components/prosemirror-editor/components/marginRail/hooks/useReferenceMarkerActions';
import AlternativeCitationSuggestion, {
    FindAlternativeBadge,
} from '../../../../../components/prosemirror-editor/components/marginRail/AlternativeCitationSuggestion';
import {getProvenanceFromDocSourceType} from './sourcesProvenance';
import SourceProvenanceChip from '../../../../../components/segmentItem/SourceProvenanceChip';

const BULK_BATCH_SIZE = 4;

function rowKey(citation) {
    return citation.localId ?? citation.referenceMarkId;
}

const AboveUCitationsView = () => {
    const {reportId} = useParams();
    const ydoc = useYDocStore((s) => s.ydoc);
    const yReferenceMarks = useYDocStore((s) => s.yReferenceMarks);
    const {citations, loading} = useAboveUCitations();

    const [expandedKeys, setExpandedKeys] = useState(() => new Set());
    const [bulkRunning, setBulkRunning] = useState(false);
    const doneResolvers = useRef(new Map());

    const toggleExpand = useCallback((key) => {
        setExpandedKeys((prev) => {
            const next = new Set(prev);
            if (next.has(key)) {
                next.delete(key);
            } else {
                next.add(key);
            }
            return next;
        });
    }, []);

    const handleDone = useCallback((key) => {
        const resolve = doneResolvers.current.get(key);
        if (resolve) {
            resolve();
            doneResolvers.current.delete(key);
        }
    }, []);

    const handleReplace = useCallback((referenceMarkIds, oldLocalId, alternativeCitation) => {
        if (!ydoc || !yReferenceMarks) return;
        const referenceData = buildReferenceDataFromCitation(alternativeCitation);
        if (!referenceData) return;
        ydoc.transact(() => {
            referenceMarkIds.forEach((referenceMarkId) => {
                removeReferenceMark(ydoc, yReferenceMarks, referenceMarkId, oldLocalId);
                addReference(ydoc, yReferenceMarks, referenceMarkId, referenceData);
            });
        }, null);
    }, [ydoc, yReferenceMarks]);

    const handleAddAlongside = useCallback((referenceMarkIds, alternativeCitation) => {
        if (!ydoc || !yReferenceMarks) return;
        const referenceData = buildReferenceDataFromCitation(alternativeCitation);
        if (!referenceData) return;
        ydoc.transact(() => {
            referenceMarkIds.forEach((referenceMarkId) => {
                addReference(ydoc, yReferenceMarks, referenceMarkId, referenceData);
            });
        }, null);
    }, [ydoc, yReferenceMarks]);

    const handleFindAll = useCallback(async () => {
        const pending = citations.filter((c) => !expandedKeys.has(rowKey(c)));
        if (pending.length === 0) return;
        setBulkRunning(true);
        try {
            for (let i = 0; i < pending.length; i += BULK_BATCH_SIZE) {
                const batch = pending.slice(i, i + BULK_BATCH_SIZE);
                const batchPromises = batch.map((citation) => new Promise((resolve) => {
                    const key = rowKey(citation);
                    doneResolvers.current.set(key, resolve);
                    setExpandedKeys((prev) => new Set(prev).add(key));
                }));
                // eslint-disable-next-line no-await-in-loop
                await Promise.all(batchPromises);
            }
        } finally {
            setBulkRunning(false);
        }
    }, [citations, expandedKeys]);

    const countLabel = useMemo(() => {
        if (loading && citations.length === 0) return 'Scanning document…';
        return `${citations.length} citation${citations.length === 1 ? '' : 's'} above (U)`;
    }, [citations.length, loading]);

    return (
        <Stack height="100%" overflow="hidden">
            <Stack p={2} spacing={1.5} borderBottom={1} borderColor="divider">
                <Typography variant="body2" color="text.secondary">
                    Citations sourced from a document classified above (U) are listed below.
                    Finding a low-side alternative makes the citation — and your report — easier to
                    disclose and distribute.
                </Typography>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography variant="caption" color="text.secondary">{countLabel}</Typography>
                    <Button
                        size="small"
                        variant="contained"
                        startIcon={<FindReplaceIcon fontSize="small"/>}
                        onClick={handleFindAll}
                        disabled={bulkRunning || citations.length === 0}
                    >
                        {bulkRunning ? 'Finding alternatives…' : 'Find alternatives for all'}
                    </Button>
                </Stack>
            </Stack>

            <Stack flexGrow={1} overflow="auto">
                {citations.length === 0 ? (
                    <Box p={2}>
                        {loading ? (
                            <Typography variant="body2" color="text.secondary">
                                Checking source classification levels…
                            </Typography>
                        ) : (
                            <Alert severity="success" sx={{py: 0.5}}>
                                No above-(U) citations found — every source in this report is
                                already (U).
                            </Alert>
                        )}
                    </Box>
                ) : (
                    <List sx={{display: 'flex', flexDirection: 'column', gap: 0.5, p: 1}}>
                        {citations.map((citation) => {
                            const key = rowKey(citation);
                            const expanded = expandedKeys.has(key);
                            const provenance = getProvenanceFromDocSourceType(citation.docSourceType);
                            return (
                                <ListItem
                                    key={key}
                                    disableGutters
                                    sx={{
                                        flexDirection: 'column',
                                        alignItems: 'stretch',
                                        bgcolor: 'rgba(0,0,0,0.02)',
                                        borderRadius: 1,
                                        p: 0,
                                    }}
                                >
                                    <Box sx={{p: 1.5, pb: 1}}>
                                        <Stack direction="row" spacing={0.5} alignItems="center" sx={{mb: 0.5, flexWrap: 'wrap'}}>
                                            <SourceProvenanceChip provenance={provenance}/>
                                            <Chip
                                                label={citation.resolvedClassificationLevel}
                                                size="small"
                                                color="warning"
                                                sx={{height: 18, fontSize: '0.65rem', fontWeight: 700}}
                                            />
                                            {citation.usageCount > 1 && (
                                                <Chip
                                                    label={`Used ${citation.usageCount}×`}
                                                    size="small"
                                                    variant="outlined"
                                                    sx={{height: 18, fontSize: '0.65rem', fontWeight: 600}}
                                                />
                                            )}
                                            <Typography variant="subtitle2" sx={{fontWeight: 600, wordBreak: 'break-word'}}>
                                                {citation.docTitle || 'Reference'}
                                            </Typography>
                                        </Stack>
                                        {citation.content && (
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    color: 'text.secondary',
                                                    fontSize: '0.75rem',
                                                    display: '-webkit-box',
                                                    WebkitLineClamp: 2,
                                                    WebkitBoxOrient: 'vertical',
                                                    overflow: 'hidden',
                                                    mb: 1,
                                                }}
                                            >
                                                {citation.content}
                                            </Typography>
                                        )}
                                        <FindAlternativeBadge
                                            level={citation.resolvedClassificationLevel}
                                            expanded={expanded}
                                            onClick={() => toggleExpand(key)}
                                        />
                                    </Box>
                                    {expanded && (
                                        <AlternativeCitationSuggestion
                                            reference={citation}
                                            reportId={reportId}
                                            statementText={citation.content}
                                            onReplace={(oldLocalId, segment) =>
                                                handleReplace(citation.referenceMarkIds, oldLocalId, segment)}
                                            onAddAlongside={(segment) =>
                                                handleAddAlongside(citation.referenceMarkIds, segment)}
                                            onDone={() => handleDone(key)}
                                        />
                                    )}
                                </ListItem>
                            );
                        })}
                    </List>
                )}
            </Stack>
        </Stack>
    );
};

export default AboveUCitationsView;
